import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '../styles/colors';

interface Props {
  src: string;
  onDone: (blob: Blob) => void;
  onCancel: () => void;
}

export default function ImageCropModal({ src, onDone, onCancel }: Props) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const lastDist = useRef(0);

  // Circle crop size = min(container width, container height) * 0.8
  const [cropSize, setCropSize] = useState(280);

  useEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setCropSize(Math.min(rect.width, rect.height) * 0.8);
  }, []);

  const onImgLoad = useCallback(() => {
    if (!imgRef.current || !containerRef.current) return;
    const img = imgRef.current;
    const container = containerRef.current.getBoundingClientRect();
    const cs = Math.min(container.width, container.height) * 0.8;
    setCropSize(cs);

    // Fit image so shortest side fills the crop circle
    const ratio = Math.max(cs / img.naturalWidth, cs / img.naturalHeight);
    setScale(ratio);
    setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    setOffset({ x: 0, y: 0 });
  }, []);

  // Touch handlers for drag + pinch
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging) {
      const dx = e.touches[0].clientX - lastPos.current.x;
      const dy = e.touches[0].clientY - lastPos.current.y;
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (lastDist.current > 0) {
        const ratio = dist / lastDist.current;
        setScale(prev => Math.max(0.1, prev * ratio));
      }
      lastDist.current = dist;
    }
  };

  const onTouchEnd = () => {
    setDragging(false);
    lastDist.current = 0;
  };

  // Mouse handlers for desktop
  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const onMouseUp = () => setDragging(false);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale(prev => Math.max(0.1, prev * (e.deltaY > 0 ? 0.95 : 1.05)));
  };

  const handleCrop = () => {
    const canvas = document.createElement('canvas');
    const outputSize = 512;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d')!;

    const img = imgRef.current!;
    const container = containerRef.current!;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    // Calculate what part of the image is in the crop circle
    const imgDisplayW = imgSize.w * scale;
    const imgDisplayH = imgSize.h * scale;
    const imgLeft = cx - imgDisplayW / 2 + offset.x;
    const imgTop = cy - imgDisplayH / 2 + offset.y;

    // Crop circle position
    const cropLeft = cx - cropSize / 2;
    const cropTop = cy - cropSize / 2;

    // Source coordinates in original image
    const srcX = (cropLeft - imgLeft) / scale;
    const srcY = (cropTop - imgTop) / scale;
    const srcW = cropSize / scale;
    const srcH = cropSize / scale;

    // Draw circular clip
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();

    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputSize, outputSize);

    canvas.toBlob((blob) => {
      if (blob) onDone(blob);
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" style={{ backgroundColor: '#000' }}>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <button onClick={onCancel} className="text-sm font-bold" style={{ color: colors.textMuted }}>
          {isKo ? '취소' : 'Cancel'}
        </button>
        <span className="text-sm font-bold" style={{ color: colors.text }}>
          {isKo ? '사진 자르기' : 'Crop Photo'}
        </span>
        <button onClick={handleCrop} className="text-sm font-bold" style={{ color: colors.primary }}>
          {isKo ? '완료' : 'Done'}
        </button>
      </div>

      {/* Crop area */}
      <div ref={containerRef}
        className="relative flex-1 overflow-hidden"
        style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}>
        {/* Image */}
        <img ref={imgRef} src={src} alt=""
          onLoad={onImgLoad}
          className="absolute select-none"
          draggable={false}
          style={{
            left: '50%', top: '50%',
            width: imgSize.w * scale,
            height: imgSize.h * scale,
            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
          }} />

        {/* Overlay with circle cutout */}
        <svg className="pointer-events-none absolute inset-0 h-full w-full">
          <defs>
            <mask id="crop-mask">
              <rect width="100%" height="100%" fill="white" />
              <circle cx="50%" cy="50%" r={cropSize / 2} fill="black" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#crop-mask)" />
          <circle cx="50%" cy="50%" r={cropSize / 2} fill="none" stroke="white" strokeWidth="2" />
        </svg>
      </div>

      {/* Hint */}
      <div className="shrink-0 py-3 text-center"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <p className="text-xs" style={{ color: colors.textMuted }}>
          {isKo ? '드래그하여 이동, 핀치하여 확대/축소' : 'Drag to move, pinch to zoom'}
        </p>
      </div>
    </div>
  );
}
